import React, { useState } from 'react';
import { resolveOtpEscalation } from '../../services/addService.service';
import { useDialog } from '../../contexts/DialogContext';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { formatDateTime } from '../../utils/dateUtils';

const OTP_STEP_STATUSES = ['photos_submitted', 'svc_photos_submitted'];
const MIN_REASON_LENGTH = 5;

const isTrue = (v) => v === true || v === 'true';

// Mirrors server otp_stage(): tow at photos_submitted = pickup, everything else = completion.
const stageFor = (status, serviceTypeCode) => {
  if (status === 'photos_submitted' && String(serviceTypeCode || '').toUpperCase() === 'TOW') {
    return 'pickup';
  }
  return 'completion';
};

const OtpEscalationPanel = ({ detailedServiceData, onResolved }) => {
  const { toast, confirm } = useDialog();
  const { isAgent, isManager } = useRoleAccess();
  const [reason, setReason] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState('');

  const svc = detailedServiceData?.service;
  if (!svc?.id) return null;

  const meta = svc.meta_data || {};
  const escalated = isTrue(meta.completion_otp_escalated);
  const atOtpStep = OTP_STEP_STATUSES.includes(svc.status);
  if (!escalated && !atOtpStep) return null;

  const stage = stageFor(svc.status, svc.scheme_service_type?.service_type_code);
  const hasDriver = !!svc.driver_id;
  const driverName = detailedServiceData?.assigned_driver?.name;
  const canAct = isAgent || isManager;
  const trimmedReason = reason.trim();
  const reasonOk = trimmedReason.length >= MIN_REASON_LENGTH;
  const canOverride = atOtpStep && hasDriver;
  const log = Array.isArray(meta.otp_escalation_log) ? [...meta.otp_escalation_log].reverse() : [];

  const run = async (action) => {
    if (!reasonOk) {
      setError(`Enter a reason of at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    if (action === 'override') {
      const ok = await confirm({
        title: `Override customer ${stage} OTP`,
        message:
          `The driver will be allowed to continue without the customer's ${stage} OTP.\n\n` +
          `Reason: ${trimmedReason}\n\nThis is recorded against your name. Continue?`,
        confirmLabel: 'Override OTP',
      });
      if (!ok) return;
    }
    setBusyAction(action);
    setError('');
    try {
      const res = await resolveOtpEscalation(svc.id, action, trimmedReason);
      toast.success(res?.message || (action === 'clear' ? 'OTP escalation cleared' : 'OTP overridden'));
      setReason('');
      if (onResolved) onResolved();
    } catch (err) {
      setError(err?.message || 'Request failed');
    } finally {
      setBusyAction(null);
    }
  };

  const tone = escalated ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50';
  const titleTone = escalated ? 'text-red-800' : 'text-amber-800';

  return (
    <div className={`mb-4 rounded-md border px-4 py-3 ${tone}`}>
      <p className={`text-sm font-semibold ${titleTone}`}>
        {escalated ? 'Customer OTP escalated' : `Waiting for customer ${stage} OTP`}
      </p>
      <p className="mt-1 text-sm text-gray-700">
        {escalated
          ? `The driver used up all OTP attempts${meta.completion_otp_escalated_at ? ` (${formatDateTime(meta.completion_otp_escalated_at)})` : ''}. Clear the escalation so they can request a new OTP, or override the OTP if the customer cannot provide it.`
          : `The driver needs the customer's ${stage} OTP to continue. Override only if the customer cannot receive or share the code.`}
      </p>
      <p className="mt-1 text-xs text-gray-600">
        Stage: <span className="font-medium capitalize">{stage}</span>
        {' · '}Status: <span className="font-medium">{String(svc.status).replace(/_/g, ' ')}</span>
        {' · '}Driver: <span className="font-medium">{hasDriver ? (driverName || `#${svc.driver_id}`) : 'not assigned'}</span>
      </p>

      {canAct ? (
        <div className="mt-3 space-y-2">
          <label className="block text-xs font-medium text-gray-700" htmlFor={`otp-esc-reason-${svc.id}`}>
            Reason (required, recorded in the audit log)
          </label>
          <textarea
            id={`otp-esc-reason-${svc.id}`}
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="e.g. Customer confirmed on call that the driver completed the job"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            {escalated ? (
              <button
                type="button"
                onClick={() => run('clear')}
                disabled={!!busyAction || !reasonOk}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {busyAction === 'clear' ? 'Clearing…' : 'Clear escalation'}
              </button>
            ) : null}
            {canOverride ? (
              <button
                type="button"
                onClick={() => run('override')}
                disabled={!!busyAction || !reasonOk}
                className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 text-sm font-medium"
              >
                {busyAction === 'override' ? 'Overriding…' : `Override ${stage} OTP`}
              </button>
            ) : null}
          </div>
          {!canOverride ? (
            <p className="text-xs text-gray-600">
              {hasDriver
                ? 'Override is available once the job reaches a customer OTP step (after photos are submitted).'
                : 'Override needs an assigned driver.'}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 text-xs text-gray-600">Only agents and managers can clear or override customer OTPs.</p>
      )}

      {log.length > 0 ? (
        <div className="mt-3 border-t border-gray-200 pt-2">
          <p className="text-xs font-semibold text-gray-700">History</p>
          <ul className="mt-1 space-y-1">
            {log.slice(0, 5).map((entry, idx) => (
              <li key={`${entry.at}-${idx}`} className="text-xs text-gray-600">
                <span className="font-medium">{entry.action === 'override' ? `Override (${entry.stage})` : 'Cleared'}</span>
                {' by '}{entry.role || 'user'} #{entry.by}
                {entry.at ? ` · ${formatDateTime(entry.at)}` : ''}
                {entry.reason ? ` · “${entry.reason}”` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default OtpEscalationPanel;
